import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { HiOutlineBell, HiOutlineCheck, HiOutlineWifi, HiOutlineXCircle, HiClock } from 'react-icons/hi2'
import useOvacsNotifications from '../../hooks/useOvacsNotifications'
import { formatDistanceToNow } from 'date-fns'
import { Link } from 'react-router-dom'
import { useToast } from '../common/ToastContainer'

/**
 * Unified Notification Bell Component
 *
 * Features:
 * - Real-time notifications via SSE
 * - Unread badge (shows 9+ if more than 9)
 * - Connection status indicator
 * - Dropdown with 10 most recent notifications
 * - Priority indicators (colors/icons)
 * - Mark as read on click
 * - Mark all as read button
 * - View all notifications link
 * - Notification type icons
 *
 * Uses semantic tokens (bg-elevated, text-ink-*, border-border-*) so the
 * dropdown looks correct in both light + dark mode. Positioned with
 * `end-0` so it flips automatically under RTL.
 */
const OvacsNotificationBell = () => {
  const { t } = useTranslation()
  const toast = useToast()
  const {
    notifications,
    unreadCount,
    isConnected,
    connectionStatus,
    error,
    markAsRead,
    markAllAsRead,
  } = useOvacsNotifications(toast)

  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true })
    } catch {
      return dateString
    }
  }

  const getNotificationIcon = (type) => {
    const icons = {
      // Messaging
      'message': '💬',
      'broadcast': '📢',
      'mention': '@',

      // Connections
      'connection_request': '🔗',
      'connection_accepted': '✅',
      'connection_rejected': '❌',
      'invitation_sent': '📧',
      'invitation_accepted': '🎉',
      'role_updated': '🔄',

      // Documents
      'document_uploaded': '📄',
      'document_shared': '📤',
      'document_updated': '📝',
      'document_comment': '💬',

      // Cases & Sessions
      'case_created': '📋',
      'case_updated': '📝',
      'case_assigned': '👤',
      'session_created': '📅',
      'session_updated': '📅',
      'session_reminder': '⏰',

      // System
      'system': '⚙️',
      'announcement': '📣',
      'payment_reminder': '💳',
      'subscription_expiring': '⚠️',
      'subscription_expired': '🚫',

      // Contact & Support
      'contact_message': '📬',
      'support_reply': '💬',
    }
    return icons[type] || '🔔'
  }

  const getPriorityColor = (priority) => {
    const colors = {
      'low': 'text-ink-muted',
      'medium': 'text-info',
      'high': 'text-warning',
      'urgent': 'text-danger',
    }
    return colors[priority] || 'text-info'
  }

  const getPriorityBadge = (priority) => {
    if (priority === 'low' || priority === 'medium') return null

    return (
      <span className={`text-xs font-semibold ${getPriorityColor(priority)}`}>
        {priority === 'urgent' && '🔥'}
        {priority === 'high' && '⚠️'}
      </span>
    )
  }

  const handleNotificationClick = async (notification) => {
    if (!notification.is_read) {
      await markAsRead(notification.id)
    }
    setIsOpen(false)
  }

  const handleMarkAllRead = async () => {
    await markAllAsRead()
  }

  // Get connection status display
  const getConnectionStatusDisplay = () => {
    switch (connectionStatus) {
      case 'connected':
        return (
          <>
            <HiOutlineWifi className="w-3 h-3 text-success" />
            <span className="text-xs text-success font-medium">{t('notificationsPopup.live')}</span>
          </>
        )
      case 'connecting':
        return (
          <>
            <div className="w-3 h-3 border-2 border-ink-muted border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-ink-muted">{t('notificationsPopup.connecting')}</span>
          </>
        )
      case 'error':
        return (
          <>
            <HiOutlineXCircle className="w-3 h-3 text-danger" />
            <span className="text-xs text-danger">{t('notificationsPopup.connectionError')}</span>
          </>
        )
      default:
        return (
          <>
            <HiOutlineXCircle className="w-3 h-3 text-ink-muted" />
            <span className="text-xs text-ink-muted">{t('notificationsPopup.disconnected')}</span>
          </>
        )
    }
  }

  // Show most recent 10 notifications
  const recentNotifications = notifications.slice(0, 10)

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Icon Button — h-9 to match all other topbar buttons */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative h-9 w-9 inline-flex items-center justify-center text-ink-secondary hover:text-ink-primary hover:bg-surface rounded-lg transition-colors"
        title={isConnected ? t('notificationsPopup.realtimeActive') : t('notificationsPopup.realtimeDisconnected')}
        aria-label={t('nav.notifications')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        <HiOutlineBell className="w-5 h-5" />

        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -end-0.5 min-w-[18px] h-[18px] px-1 bg-danger text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-canvas">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}

        {/* Connection Status Indicator (small dot) */}
        {isConnected && unreadCount === 0 && (
          <span
            className="absolute bottom-0 end-0 w-2 h-2 bg-success rounded-full ring-2 ring-canvas"
            title="Live connection active"
          />
        )}
        {error && !isConnected && unreadCount === 0 && (
          <span
            className="absolute bottom-0 end-0 w-2 h-2 bg-danger rounded-full ring-2 ring-canvas"
            title="Connection error"
          />
        )}
      </button>

      {/* Dropdown Panel — end-0 so it flips under RTL */}
      {isOpen && (
        <div className="absolute end-0 mt-2 w-96 bg-elevated rounded-xl shadow-token-lg border border-border z-50">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border-subtle">
            <div>
              <h3 className="text-lg font-bold text-ink-primary">
                {t('notificationsPopup.title')}
                {unreadCount > 0 && (
                  <span className="ms-2 text-sm text-ink-muted font-normal">
                    ({t('notificationsPopup.unreadSuffix', { count: unreadCount })})
                  </span>
                )}
              </h3>
              {/* Connection Status */}
              <div className="flex items-center gap-1 mt-1">
                {getConnectionStatusDisplay()}
              </div>
            </div>

            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-sm text-brand hover:text-brand-hover font-medium flex items-center gap-1 px-2 py-1 rounded-md hover:bg-brand-soft transition-colors"
              >
                <HiOutlineCheck className="w-4 h-4" />
                {t('notificationsPopup.markAllRead')}
              </button>
            )}
          </div>

          {/* Notifications List */}
          <div className="max-h-96 overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="p-8 text-center">
                <HiOutlineBell className="w-12 h-12 text-ink-disabled mx-auto mb-2" />
                <p className="text-ink-muted">{t('notificationsPopup.empty')}</p>
              </div>
            ) : (
              <div className="divide-y divide-border-subtle">
                {recentNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-muted transition-colors cursor-pointer ${
                      !notification.is_read ? 'bg-brand-soft/50' : ''
                    }`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    {notification.link ? (
                      <Link to={notification.link} className="block">
                        <NotificationContent
                          notification={notification}
                          formatDate={formatDate}
                          getNotificationIcon={getNotificationIcon}
                          getPriorityBadge={getPriorityBadge}
                          t={t}
                        />
                      </Link>
                    ) : (
                      <NotificationContent
                        notification={notification}
                        formatDate={formatDate}
                        getNotificationIcon={getNotificationIcon}
                        getPriorityBadge={getPriorityBadge}
                        t={t}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {recentNotifications.length > 0 && (
            <div className="p-3 border-t border-border-subtle bg-canvas/40 rounded-b-xl">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="text-sm text-brand hover:text-brand-hover font-medium block text-center"
              >
                {t('notificationsPopup.viewAll')}
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// Notification Content Component
const NotificationContent = ({ notification, formatDate, getNotificationIcon, getPriorityBadge, t }) => (
  <div className="flex items-start gap-3">
    <div className="text-2xl flex-shrink-0">
      {getNotificationIcon(notification.notification_type)}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1">
        <p className="font-semibold text-ink-primary text-sm truncate">
          {notification.title}
        </p>
        {getPriorityBadge(notification.priority)}
      </div>
      {notification.message && (
        <p
          className="text-sm text-ink-secondary mb-2 break-words"
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {notification.message}
        </p>
      )}
      <div className="flex items-center gap-2 text-xs text-ink-muted flex-wrap">
        {notification.sender_name && (
          <>
            <span>{t('notificationsPopup.from', { name: notification.sender_name })}</span>
            <span>•</span>
          </>
        )}
        <HiClock className="w-3 h-3" />
        <span>{formatDate(notification.created_at)}</span>
        {notification.notification_type_display && (
          <>
            <span>•</span>
            <span className="text-ink-muted">{notification.notification_type_display}</span>
          </>
        )}
      </div>
      {/* Action Button (if present) */}
      {notification.action_label && (
        <div className="mt-2">
          <button className="text-xs px-3 py-1 bg-brand text-white rounded-md hover:bg-brand-hover transition-colors">
            {notification.action_label}
          </button>
        </div>
      )}
    </div>
    {!notification.is_read && (
      <div className="w-2 h-2 bg-brand rounded-full flex-shrink-0 mt-1" />
    )}
  </div>
)

export default OvacsNotificationBell
